import { auth } from '../lib/firebase';

// API Configuration
const API_BASE_URL = 'https://api.cvlogs.bytelogs.cloud';
// const API_BASE_URL = 'http://127.0.0.1:8000';
const DEVELOPMENT_MODE = import.meta.env.VITE_DEVELOPMENT_MODE === 'true';

// Helper function to get Firebase ID token
let tokenGetPromise = null;

const getAuthToken = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }
    
    if (!tokenGetPromise) {
      tokenGetPromise = auth.currentUser.getIdToken().finally(() => {
        tokenGetPromise = null;
      });
    }
    return tokenGetPromise;
  } catch (error) {
    throw error;
  }
};

// Helper function to create development token (for testing)
const createDevelopmentToken = (uid, email, name = 'Test User') => {
  const tokenData = {
    uid,
    email,
    name
  };
  const jsonStr = JSON.stringify(tokenData);
  const encodedToken = btoa(unescape(encodeURIComponent(jsonStr)));
  return encodedToken;
};

let tokenRefreshPromise = null;

const getFreshToken = async (user) => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = user.getIdToken(true).finally(() => {
      tokenRefreshPromise = null;
    });
  }
  return tokenRefreshPromise;
};

// Generic API request function
const apiRequest = async (endpoint, options = {}, isRetry = false, customBaseUrl = null) => {
  try {
    let token = null;
    
    if (DEVELOPMENT_MODE && !auth.currentUser) {
      // Use development token for testing when no user is authenticated
      token = createDevelopmentToken('dev-user-1', 'test@example.com', 'Test User');
    } else if (auth.currentUser) {
      // Use Firebase ID token
      token = await getAuthToken();
    }

    const baseUrlToUse = customBaseUrl || API_BASE_URL;
    const url = `${baseUrlToUse}${endpoint}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
    };
    
    const response = await fetch(url, config);
    
    if (response.status === 401 && !isRetry && auth.currentUser) {
      // Force refresh token and retry once
      console.log('API returned 401. Force refreshing Firebase ID token and retrying...');
      try {
        const freshToken = await getFreshToken(auth.currentUser);
        // Retry request with fresh token
        const newConfig = {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${freshToken}`,
            ...options.headers,
          }
        };
        const retryResponse = await fetch(url, newConfig);
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
        }
        if (retryResponse.status === 204) {
          return null;
        }
        return await retryResponse.json();
      } catch (refreshError) {
        console.error('Failed to refresh token and retry:', refreshError);
        // Fall back to throwing the original 401 error
      }
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle 204 No Content responses (common for DELETE operations)
    if (response.status === 204) {
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Public API request function (no authentication required)
const publicApiRequest = async (endpoint, options = {}) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };
    
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Export utility functions
export { getAuthToken };

export const portfolioSettingsApi = {
  get: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/portfolio-settings`);
  },

  save: async (resumeId, settings) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/portfolio-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
};

// Resume Information API functions (new endpoints)
export const resumeInfoApi = {
  // Get all resumes for the authenticated user
  list: async () => {
    return apiRequest('/cv-info/resumes');
  },

  // Get a specific resume information
  get: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}`);
  },

  // Create new resume information
  create: async (resumeData) => {
    const backendData = {
      resume_name: resumeData.resume_name || '',
      name: resumeData.name || '',
      email: resumeData.email || '',
      phone: resumeData.phone || '',
      city: resumeData.city || '',
      country: resumeData.country || '',
      address: resumeData.address || '',
      links: resumeData.links || {},
      summary: resumeData.summary || ''
    };

    return apiRequest('/cv-info/resumes', {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },


  // Update resume information
  update: async (resumeId, resumeData) => {
    const backendData = {
      resume_name: resumeData.resume_name || '',
      name: resumeData.name || '',
      email: resumeData.email || '',
      phone: resumeData.phone || '',
      city: resumeData.city || '',
      country: resumeData.country || '',
      address: resumeData.address || '',
      links: resumeData.links || {},
      summary: resumeData.summary || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete resume information
  delete: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}`, {
      method: 'DELETE',
    });
  }
};

// Licenses/Certifications API functions (nested under resumes)
export const licensesApi = {
  // Get all licenses for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/licenses`);
  },

  // Get a specific license
  get: async (resumeId, licenseId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/licenses/${licenseId}`);
  },

  // Create new license
  create: async (resumeId, licenseData) => {
    const backendData = {

      title: licenseData.title || licenseData.name || '',
      issuer: licenseData.issuer || '',
      issue_date: licenseData.issue_date || licenseData.date || '',
      expiry_date: licenseData.expiry_date || licenseData.expiryDate || null,
      credential_id: licenseData.credential_id || licenseData.credentialId || '',
      credential_url: licenseData.credential_url || licenseData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/licenses`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update license
  update: async (resumeId, licenseId, licenseData) => {
    const backendData = {
      title: licenseData.title || licenseData.name || '',
      issuer: licenseData.issuer || '',
      issue_date: licenseData.issue_date || licenseData.date || '',
      expiry_date: licenseData.expiry_date || licenseData.expiryDate || null,
      credential_id: licenseData.credential_id || licenseData.credentialId || '',
      credential_url: licenseData.credential_url || licenseData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/licenses/${licenseId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },


  // Delete license
  delete: async (resumeId, licenseId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/licenses/${licenseId}`, {
      method: 'DELETE',
    });

  }
};

// Awards API functions (nested under resumes)
export const awardsApi = {
  // Get all awards for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/awards`);
  },

  // Get a specific award
  get: async (resumeId, awardId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/awards/${awardId}`);
  },

  // Create new award
  create: async (resumeId, awardData) => {
    const backendData = {
      title: awardData.title || '',
      organization: awardData.organization || awardData.issuer || '',
      date_received: awardData.date_received || awardData.date || '',
      description: awardData.description || '',
      link: awardData.link || awardData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/awards`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update award
  update: async (resumeId, awardId, awardData) => {
    const backendData = {
      title: awardData.title || '',
      organization: awardData.organization || awardData.issuer || '',
      date_received: awardData.date_received || awardData.date || '',
      description: awardData.description || '',
      link: awardData.link || awardData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/awards/${awardId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete award
  delete: async (resumeId, awardId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/awards/${awardId}`, {
      method: 'DELETE',
    });
  }
};

// Volunteer API functions (nested under resumes)
export const volunteerApi = {
  // Get all volunteer experiences for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/volunteer`);
  },

  // Get a specific volunteer experience
  get: async (resumeId, volunteerId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/volunteer/${volunteerId}`);
  },

  // Create new volunteer experience
  create: async (resumeId, volunteerData) => {
    const backendData = {
      organization: volunteerData.organization || '',
      role: volunteerData.role || '',
      cause: volunteerData.cause || '',
      start_date: volunteerData.start_date || volunteerData.startDate || '',
      end_date: volunteerData.end_date || volunteerData.endDate || null,
      description: volunteerData.description || '',
      link: volunteerData.link || volunteerData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/volunteer`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update volunteer experience
  update: async (resumeId, volunteerId, volunteerData) => {
    const backendData = {
      organization: volunteerData.organization || '',
      role: volunteerData.role || '',
      cause: volunteerData.cause || '',
      start_date: volunteerData.start_date || volunteerData.startDate || '',
      end_date: volunteerData.end_date || volunteerData.endDate || null,
      description: volunteerData.description || '',
      link: volunteerData.link || volunteerData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/volunteer/${volunteerId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete volunteer experience
  delete: async (resumeId, volunteerId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/volunteer/${volunteerId}`, {
      method: 'DELETE',
    });
  }
};

// Recommendations API functions (nested under resumes)
export const recommendationsApi = {
  // Get all recommendations for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/recommendations`);
  },

  // Get a specific recommendation
  get: async (resumeId, recommendationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/recommendations/${recommendationId}`);
  },

  // Create new recommendation
  create: async (resumeId, recommendationData) => {
    const backendData = {
      recommender_name: recommendationData.recommender_name || recommendationData.name || '',
      recommender_position: recommendationData.recommender_position || recommendationData.position || '',
      organization: recommendationData.organization || '',
      contact_info: recommendationData.contact_info || recommendationData.contact || '',
      text: recommendationData.text || recommendationData.recommendation || '',
      link: recommendationData.link || recommendationData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/recommendations`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update recommendation
  update: async (resumeId, recommendationId, recommendationData) => {
    const backendData = {
      recommender_name: recommendationData.recommender_name || recommendationData.name || '',
      recommender_position: recommendationData.recommender_position || recommendationData.position || '',
      organization: recommendationData.organization || '',
      contact_info: recommendationData.contact_info || recommendationData.contact || '',
      text: recommendationData.text || recommendationData.recommendation || '',
      link: recommendationData.link || recommendationData.url || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/recommendations/${recommendationId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete recommendation
  delete: async (resumeId, recommendationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/recommendations/${recommendationId}`, {
      method: 'DELETE',
    });
  }
};

// Publications API functions (nested under resumes)
export const publicationsApi = {
  // Get all publications for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/publications`);
  },

  // Get a specific publication
  get: async (resumeId, publicationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/publications/${publicationId}`);
  },

  // Create new publication
  create: async (resumeId, publicationData) => {
    const backendData = {
      title: publicationData.title || '',
      publisher: publicationData.publisher || '',
      publication_date: publicationData.publication_date || publicationData.date || '',
      link: publicationData.link || publicationData.url || '',
      description: publicationData.description || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/publications`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update publication
  update: async (resumeId, publicationId, publicationData) => {
    const backendData = {
      title: publicationData.title || '',
      publisher: publicationData.publisher || '',
      publication_date: publicationData.publication_date || publicationData.date || '',
      link: publicationData.link || publicationData.url || '',
      description: publicationData.description || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/publications/${publicationId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete publication
  delete: async (resumeId, publicationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/publications/${publicationId}`, {
      method: 'DELETE',
    });
  }
};

// Interests API functions (nested under resumes)
export const interestsApi = {
  // Get all interests for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/interests`);
  },

  // Get a specific interest
  get: async (resumeId, interestId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/interests/${interestId}`);
  },

  // Create new interest
  create: async (resumeId, interestData) => {
    const backendData = {
      interest_name: interestData.interest_name || interestData.name || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/interests`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update interest
  update: async (resumeId, interestId, interestData) => {
    const backendData = {
      interest_name: interestData.interest_name || interestData.name || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/interests/${interestId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete interest
  delete: async (resumeId, interestId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/interests/${interestId}`, {
      method: 'DELETE',
    });
  }
};

// Education API functions (nested under resumes)
export const educationApi = {
  // Get all education entries for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/education`);
  },

  // Get a specific education entry
  get: async (resumeId, educationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/education/${educationId}`);
  },

  // Create new education entry
  create: async (resumeId, educationData) => {
    const backendData = {
      organization_name: educationData.organization_name || educationData.school || '',
      degree: educationData.degree || '',
      major: educationData.major || educationData.field || '',
      city: educationData.city || '',
      country: educationData.country || '',
      start_date: educationData.start_date || educationData.startDate || '',
      end_date: educationData.end_date || educationData.endDate || null
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/education`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update education entry
  update: async (resumeId, educationId, educationData) => {
    const backendData = {
      organization_name: educationData.organization_name || educationData.school || '',
      degree: educationData.degree || '',
      major: educationData.major || educationData.field || '',
      city: educationData.city || '',
      country: educationData.country || '',
      start_date: educationData.start_date || educationData.startDate || '',
      end_date: educationData.end_date || educationData.endDate || null
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/education/${educationId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete education entry
  delete: async (resumeId, educationId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/education/${educationId}`, {
      method: 'DELETE',
    });
  }
};

// Experience API functions (nested under resumes)
export const experienceApi = {
  // Get all experience entries for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/experience`);
  },

  // Get a specific experience entry
  get: async (resumeId, experienceId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/experience/${experienceId}`);
  },

  // Create new experience entry
  create: async (resumeId, experienceData) => {
    const backendData = {
      organization: experienceData.organization || experienceData.company || '',
      job_title: experienceData.job_title || experienceData.position || '',
      city: experienceData.city || '',
      country: experienceData.country || '',
      work_type: experienceData.work_type || 'fulltime',
      start_date: experienceData.start_date || experienceData.startDate || '',
      end_date: experienceData.end_date || experienceData.endDate || null,
      description: experienceData.description || '',
      links: experienceData.links || {}
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/experience`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update experience entry
  update: async (resumeId, experienceId, experienceData) => {
    const backendData = {
      organization: experienceData.organization || experienceData.company || '',
      job_title: experienceData.job_title || experienceData.position || '',
      city: experienceData.city || '',
      country: experienceData.country || '',
      work_type: experienceData.work_type || 'fulltime',
      start_date: experienceData.start_date || experienceData.startDate || '',
      end_date: experienceData.end_date || experienceData.endDate || null,
      description: experienceData.description || '',
      links: experienceData.links || {}
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/experience/${experienceId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete experience entry
  delete: async (resumeId, experienceId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/experience/${experienceId}`, {
      method: 'DELETE',
    });
  }
};

// Projects API functions (nested under resumes)
export const projectsApi = {
  // Get all projects for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/projects`);
  },

  // Get a specific project
  get: async (resumeId, projectId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/projects/${projectId}`);
  },

  // Create new project
  create: async (resumeId, projectData) => {
    const backendData = {
      title: projectData.title || '',
      description: projectData.description || '',
      technologies: projectData.technologies || '',
      links: projectData.links || {}
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/projects`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update project
  update: async (resumeId, projectId, projectData) => {
    const backendData = {
      title: projectData.title || '',
      description: projectData.description || '',
      technologies: projectData.technologies || '',
      links: projectData.links || {}
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete project
  delete: async (resumeId, projectId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/projects/${projectId}`, {
      method: 'DELETE',
    });
  }
};

// Training API functions (nested under resumes)
export const trainingApi = {
  // Get all training courses for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/training`);
  },

  // Get a specific training course
  get: async (resumeId, trainingId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/training/${trainingId}`);
  },

  // Create new training course
  create: async (resumeId, trainingData) => {
    const backendData = {
      title: trainingData.title || '',
      organization: trainingData.organization || '',
      taken_date: trainingData.taken_date || trainingData.date || '',
      certificate_link: trainingData.certificate_link || trainingData.link || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/training`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update training course
  update: async (resumeId, trainingId, trainingData) => {
    const backendData = {
      title: trainingData.title || '',
      organization: trainingData.organization || '',
      taken_date: trainingData.taken_date || trainingData.date || '',
      certificate_link: trainingData.certificate_link || trainingData.link || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/training/${trainingId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete training course
  delete: async (resumeId, trainingId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/training/${trainingId}`, {
      method: 'DELETE',
    });
  }
};

// Skills API functions (nested under resumes)
export const skillsApi = {
  // Get all skills for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/skills`);
  },

  // Get a specific skill
  get: async (resumeId, skillId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/skills/${skillId}`);
  },

  // Create new skill
  create: async (resumeId, skillData) => {
    const backendData = {
      name: skillData.name || '',
      type: skillData.type || 'technical'
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/skills`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update skill
  update: async (resumeId, skillId, skillData) => {
    const backendData = {
      name: skillData.name || '',
      type: skillData.type || 'technical'
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/skills/${skillId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete skill
  delete: async (resumeId, skillId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/skills/${skillId}`, {
      method: 'DELETE',
    });
  }
};

// Languages API functions (nested under resumes)
export const languagesApi = {
  // Get all languages for a resume
  list: async (resumeId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/languages`);
  },

  // Get a specific language
  get: async (resumeId, languageId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/languages/${languageId}`);
  },

  // Create new language
  create: async (resumeId, languageData) => {
    const backendData = {
      language: languageData.language || languageData.name || '',
      level: languageData.level || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/languages`, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  },

  // Update language
  update: async (resumeId, languageId, languageData) => {
    const backendData = {
      language: languageData.language || languageData.name || '',
      level: languageData.level || ''
    };

    return apiRequest(`/cv-info/resumes/${resumeId}/languages/${languageId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  },

  // Delete language
  delete: async (resumeId, languageId) => {
    return apiRequest(`/cv-info/resumes/${resumeId}/languages/${languageId}`, {
      method: 'DELETE',
    });
  }
};

// User Jobs API (Job Library)
export const userJobsApi = {
  // Save a job to user's library
  saveJob: async (jobSourceTable, jobSourceId, notes = null) => {
    const requestBody = {
      job_source: jobSourceTable,
      job_source_id: jobSourceId,
      notes: notes
    };

    return apiRequest('/user-jobs/save', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  },

  // Get all saved jobs for the current user
  getLibrary: async (applicationStatus = null, jobSourceTable = null, limit = 100, offset = 0) => {
    const params = new URLSearchParams();
    if (applicationStatus) params.append('application_status', applicationStatus);
    if (jobSourceTable) params.append('job_source_table', jobSourceTable);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return apiRequest(`/user-jobs/library?${params.toString()}`);
  },

  // Get all saved jobs with full details
  getLibraryWithDetails: async (applicationStatus = null, jobSourceTable = null, limit = 50, offset = 0) => {
    const params = new URLSearchParams();
    if (applicationStatus) params.append('application_status', applicationStatus);
    if (jobSourceTable) params.append('job_source_table', jobSourceTable);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return apiRequest(`/user-jobs/library/with-details/all?${params.toString()}`);
  },

  // Get a specific saved job with details
  getJobDetail: async (savedJobId) => {
    return apiRequest(`/user-jobs/library/${savedJobId}`);
  },

  // Remove a job from library
  deleteJob: async (savedJobId) => {
    return apiRequest(`/user-jobs/library/${savedJobId}`, {
      method: 'DELETE',
    });
  },

  // Update job notes
  updateNotes: async (savedJobId, notes) => {
    const requestBody = { notes };

    return apiRequest(`/user-jobs/library/${savedJobId}/notes`, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
    });
  },

  // Update application status
  updateStatus: async (savedJobId, applicationStatus) => {
    const requestBody = { application_status: applicationStatus };

    return apiRequest(`/user-jobs/library/${savedJobId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
    });
  },

  // Get user job statistics
  getStatistics: async () => {
    return apiRequest('/user-jobs/statistics');
  }
};

// CV Generation API functions
export const cvGenerationApi = {
  // Generate AI-enhanced CV based on resume data
  generate: async (resumeId, targetJob = {}) => {
    const requestBody = {
      job_title: targetJob.job_title || '',
      job_description: targetJob.job_description || ''
    };

    return apiRequest(`/cv-generation/${resumeId}`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  },

  // Get the latest AI-enhanced CV for a resume
  getLatest: async (resumeId) => {
    return apiRequest(`/cv-generation/${resumeId}`);
  },

  // Get all CV generations for a resume
  getAll: async (resumeId) => {
    return apiRequest(`/cv-generation/${resumeId}/generations`);
  },

  // Get a specific CV generation by generation ID
  getSpecific: async (resumeId, generationId) => {
    return apiRequest(`/cv-generation/${resumeId}/generations/${generationId}`);
  },

  // AI Enhancement APIs
  // Update professional summary
  enhanceSummary: async (resumeId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/summary`, {
      method: 'PUT',
      body: JSON.stringify({ prompt }),
    });
  },

  // Update experience description
  enhanceExperience: async (resumeId, experienceId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/experience`, {
      method: 'PUT',
      body: JSON.stringify({ experience_id: experienceId, prompt }),
    });
  },

  // Update project description
  enhanceProject: async (resumeId, projectId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/project`, {
      method: 'PUT',
      body: JSON.stringify({ project_id: projectId, prompt }),
    });
  },

  // Update skills
  enhanceSkills: async (resumeId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/skills`, {
      method: 'PUT',
      body: JSON.stringify({ prompt }),
    });
  },

  // Update volunteer experience
  enhanceVolunteer: async (resumeId, volunteerId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/volunteer`, {
      method: 'PUT',
      body: JSON.stringify({ volunteer_id: volunteerId, prompt }),
    });
  },

  // Update award description
  enhanceAward: async (resumeId, awardId, prompt) => {
    return apiRequest(`/cv-generation/${resumeId}/awards`, {
      method: 'PUT',
      body: JSON.stringify({ award_id: awardId, prompt }),
    });
  },

  // Get saved AI-generated content for a resume
  getAIContent: async (resumeId) => {
    return apiRequest(`/cv-generation/${resumeId}/ai-content`);
  },

  // Poll the status of a background full-CV generation job
  getJobStatus: async (jobId) => {
    return apiRequest(`/cv-generation/jobs/${jobId}`);
  }
};

// Job Search API
export const jobSearchApi = {
  // Search for jobs
  searchJobs: async (searchParams, forceRefresh = false) => {
    return apiRequest(`/jobs-search/search?force_refresh=${forceRefresh}`, {
      method: 'POST',
      body: JSON.stringify(searchParams),
    });
  },

  // Get search history
  getSearchHistory: async (limit = 20, offset = 0) => {
    return apiRequest(`/jobs-search/history?limit=${limit}&offset=${offset}`);
  },

  // Get search detail by ID
  getSearchDetail: async (searchId) => {
    return apiRequest(`/jobs-search/${searchId}`);
  },

  // Refresh search
  refreshSearch: async (searchId) => {
    return apiRequest(`/jobs-search/${searchId}/refresh`, {
      method: 'POST',
    });
  }
};

// Remote Jobs API
export const remoteJobsApi = {
  // Get all job categories
  getCategories: async () => {
    return apiRequest('/remote-jobs/categories');
  },

  // Get all job types
  getJobTypes: async () => {
    return apiRequest('/remote-jobs/job-types');
  },

  // Get job statistics
  getStats: async () => {
    return apiRequest('/remote-jobs/stats');
  },

  // Get recent jobs
  getRecentJobs: async (limit = 10) => {
    return apiRequest(`/remote-jobs/recent/latest?limit=${limit}`);
  },

  // Get job detail
  getJobDetail: async (jobId) => {
    return apiRequest(`/remote-jobs/${jobId}`);
  },

  // Search remote jobs with filters
  searchJobs: async (searchParams) => {
    const requestBody = {
      query: searchParams.query || null,
      category: searchParams.category || null,
      job_type: searchParams.job_type || null,
      location: searchParams.location || null,
      tags: searchParams.tags || null,
      limit: searchParams.limit || 20,
      offset: searchParams.offset || 0
    };

    return apiRequest('/remote-jobs/search', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }
};

// Advanced Search API
export const advancedSearchApi = {
  // Search for jobs using Advanced Search API
  search: async (searchParams) => {
    const requestBody = {
      country: searchParams.country || 'ae',
      date_posted: searchParams.date_posted || 'week',
      num_pages: searchParams.num_pages || 1,
      page: searchParams.page || 1,
      query: searchParams.query || '',
      remote_jobs_only: searchParams.remote_jobs_only || false
    };

    return apiRequest('/advanced-search/search', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  },

  // Get search history
  getHistory: async (limit = 10, offset = 0) => {
    return apiRequest(`/advanced-search/history?limit=${limit}&offset=${offset}`);
  },

  // Get search detail by ID
  getSearchDetail: async (searchId) => {
    return apiRequest(`/advanced-search/history/${searchId}`);
  },

  // Delete search from history
  deleteSearch: async (searchId) => {
    return apiRequest(`/advanced-search/history/${searchId}`, {
      method: 'DELETE',
    });
  },

  // Get job detail
  getJobDetail: async (jobId) => {
    return apiRequest(`/advanced-search/jobs/${jobId}`);
  }
};

// Job Matching API
export const jobMatchingApi = {
  // Analyze detailed job match with AI
  matchJob: async (resumeId, jobDescription) => {
    console.log('🎯 Analyzing job match:', { resumeId, jobDescription: jobDescription.substring(0, 50) + '...' });
    
    const requestBody = {
      resume_id: resumeId,
      job_description: jobDescription
    };

    return apiRequest('/user-jobs/match-job', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  },

  // Quick match using BERT embeddings
  quickMatch: async (resumeId, jobSource, jobSourceId) => {
    const requestBody = {
      resume_id: resumeId,
      job_source: jobSource,
      job_source_id: jobSourceId
    };

    return apiRequest('/user-jobs/quick-match', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }
};

// Cover Letter API
export const coverLetterApi = {
  // Generate a new cover letter
  generateCoverLetter: async (data) => {
    console.log('📝 Generating cover letter:', { 
      resumeId: data.resume_id, 
      company: data.company_name,
      jobTitle: data.job_title 
    });
    
    return apiRequest('/user-jobs/cover-letters/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get all cover letters for the current user
  getUserCoverLetters: async (resumeId = null, limit = 50, offset = 0) => {
    let endpoint = `/user-jobs/cover-letters?limit=${limit}&offset=${offset}`;
    if (resumeId) {
      endpoint += `&resume_id=${resumeId}`;
    }
    return apiRequest(endpoint);
  },

  // Get a specific cover letter by ID
  getCoverLetterById: async (coverLetterId) => {
    return apiRequest(`/user-jobs/cover-letters/${coverLetterId}`);
  },

  // Update/edit a cover letter
  updateCoverLetter: async (coverLetterId, coverLetterContent) => {
    return apiRequest(`/user-jobs/cover-letters/${coverLetterId}`, {
      method: 'PATCH',
      body: JSON.stringify({ cover_letter_content: coverLetterContent }),
    });
  },

  // Regenerate a cover letter with new instructions
  regenerateCoverLetter: async (coverLetterId, customInstructions = null) => {
    const body = customInstructions ? { custom_instructions: customInstructions } : {};
    return apiRequest(`/user-jobs/cover-letters/${coverLetterId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // Delete a cover letter
  deleteCoverLetter: async (coverLetterId) => {
    return apiRequest(`/user-jobs/cover-letters/${coverLetterId}`, {
      method: 'DELETE',
    });
  }
}; 

// User Profile API functions
export const userProfileApi = {
  // Get current user profile
  getProfile: async () => {
    return apiRequest('/user/profile');
  },

  // Get user CV count
  getCvCount: async () => {
    return apiRequest('/user/cv-count');
  }
};

// ============================================================
// AI Career Agent API
// Streaming SSE protocol with typed events:
//   session | log | plan | insight | proposal | token | usage | done | error
// ============================================================
export const agentApi = {
  /**
   * Stream a message to the AI Career Agent.
   * Calls onEvent(evt) for every parsed SSE event in real time.
   * Returns a promise resolving with { conversationId, messageId, proposals }.
   */
  chat: async ({
    message,
    conversationId = null,
    resumeId = null,
    jobDescription = null,
    autoApply = null,
    askClarifying = null,
    onEvent = () => {},
    signal = null,
  }) => {
    let token;
    if (DEVELOPMENT_MODE && !auth.currentUser) {
      token = createDevelopmentToken('dev-user-1', 'test@example.com', 'Test User');
    } else {
      token = await getAuthToken();
    }

    const body = {
      message,
      ...(conversationId && { conversation_id: conversationId }),
      ...(resumeId && { resume_id: resumeId }),
      ...(jobDescription && { job_description: jobDescription }),
      ...(autoApply !== null && { auto_apply: autoApply }),
      ...(askClarifying !== null && { ask_clarifying: askClarifying }),
    };

    const response = await fetch(`${API_BASE_URL}/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const summary = { conversationId, messageId: null, proposals: [] };

    const handle = (evt) => {
      if (!evt || !evt.type) return;
      if (evt.type === 'session') summary.conversationId = evt.session_id;
      if (evt.type === 'done') {
        summary.messageId = evt.message_id ?? summary.messageId;
        if (evt.conversation_id) summary.conversationId = evt.conversation_id;
      }
      if (evt.type === 'proposal') summary.proposals.push(evt.proposal);
      onEvent(evt);
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line; events here are single-line `data:`.
        let nlIndex;
        while ((nlIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, nlIndex).trim();
          buffer = buffer.slice(nlIndex + 1);
          if (!line.startsWith('data:')) continue;
          const dataStr = line.slice(5).trim();
          if (!dataStr) continue;
          try {
            handle(JSON.parse(dataStr));
          } catch {
            /* ignore malformed partial frames */
          }
        }
      }
      // Flush any trailing buffered frame
      const tail = buffer.trim();
      if (tail.startsWith('data:')) {
        try { handle(JSON.parse(tail.slice(5).trim())); } catch { /* noop */ }
      }
    } finally {
      reader.releaseLock();
    }

    return summary;
  },

  // Conversations
  listConversations: async () => apiRequest('/agent/conversations'),
  createConversation: async (resumeId = null, title = null) =>
    apiRequest('/agent/conversations', {
      method: 'POST',
      body: JSON.stringify({ resume_id: resumeId, title }),
    }),
  getMessages: async (conversationId) =>
    apiRequest(`/agent/conversations/${conversationId}/messages`),
  deleteConversation: async (conversationId) =>
    apiRequest(`/agent/conversations/${conversationId}`, { method: 'DELETE' }),
  setAutoApply: async (conversationId, autoApply) =>
    apiRequest(`/agent/conversations/${conversationId}/auto-apply`, {
      method: 'PUT',
      body: JSON.stringify({ auto_apply: autoApply }),
    }),

  // Proposals
  listProposals: async ({ resumeId = null, status = null } = {}) => {
    const params = new URLSearchParams();
    if (resumeId) params.append('resume_id', resumeId);
    if (status) params.append('status_filter', status);
    const qs = params.toString();
    return apiRequest(`/agent/proposals${qs ? `?${qs}` : ''}`);
  },
  decideProposal: async (proposalId, action) =>
    apiRequest(`/agent/proposals/${proposalId}/decision`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  bulkDecide: async (proposalIds, action) =>
    apiRequest('/agent/proposals/bulk-decision', {
      method: 'POST',
      body: JSON.stringify({ proposal_ids: proposalIds, action }),
    }),

  // Memory
  getMemory: async () => apiRequest('/agent/memory'),
  deleteMemory: async (memoryId) =>
    apiRequest(`/agent/memory/${memoryId}`, { method: 'DELETE' }),

  // Resume versions
  listVersions: async (resumeId) => apiRequest(`/agent/resumes/${resumeId}/versions`),
  revertVersion: async (versionId) =>
    apiRequest(`/agent/versions/${versionId}/revert`, { method: 'POST' }),

  health: async () => apiRequest('/agent/health'),
};

/**
 * Backward-compatible chatbot API.
 * The standalone AI tool pages (CV Translator, ATS Matcher, Interview
 * Simulator, etc.) use this as a one-shot AI completion proxy. It now runs on
 * the new agent backend via POST /agent/complete. Session methods are no-op
 * shims kept for compatibility — the dedicated chat UI lives in agentApi.
 */
export const chatbotApi = {
  chat: async (message, sessionId = null, resumeId = null) => {
    const data = await apiRequest('/agent/complete', {
      method: 'POST',
      body: JSON.stringify({
        message,
        ...(resumeId && { resume_id: resumeId }),
      }),
    });
    // Preserve the legacy shape consumers expect: { response, session_id }
    return { response: data?.response ?? '', session_id: sessionId };
  },
  createSession: async () => ({ session_id: null }),
  listSessions: async () => [],
  getHistory: async () => ({ messages: [] }),
  deleteSession: async () => null,
};

export const interviewApi = {
  start: async (data) => {
    return apiRequest('/agent/tools/interview/start', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  reply: async (data) => {
    return apiRequest('/agent/tools/interview/reply', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  end: async (data) => {
    return apiRequest('/agent/tools/interview/end', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  }
};

export const careerToolsApi = {
  analyzeSkillGap: async (data) => {
    return apiRequest('/agent/tools/skill-gap', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  matchAts: async (data) => {
    return apiRequest('/agent/tools/ats-matcher', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  optimizeLinkedIn: async (data) => {
    return apiRequest('/agent/tools/linkedin-optimizer', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  analyzeCulturalFit: async (data) => {
    return apiRequest('/agent/tools/cultural-fit', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  predictSalary: async (data) => {
    return apiRequest('/agent/tools/salary-predictor', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  generateProjects: async (data) => {
    return apiRequest('/agent/tools/project-generator', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  translateCv: async (data) => {
    return apiRequest('/agent/tools/translate-cv', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  generateCareerRoadmap: async (data) => {
    return apiRequest('/agent/tools/career-roadmap', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  },
  generateFreelancePitch: async (data) => {
    return apiRequest('/agent/tools/freelance-pitch', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false, 'http://127.0.0.1:8000');
  }
};

// Subscription Plans API functions
export const subscriptionApi = {
  // Get all subscription plans (public endpoint, no auth required)
  getPlans: async (activeOnly = true) => {
    const params = activeOnly ? '?active_only=true' : '';
    return publicApiRequest(`/subscription/plans${params}`);
  },

  // Initialize subscription plans (admin/setup)
  initializePlans: async () => {
    return apiRequest('/subscription/initialize-plans', {
      method: 'POST'
    });
  },

  // Create a Stripe/Paymob checkout session for subscription
  createSubscriptionSession: async (planId, successUrl, cancelUrl, customerInfo = {}, couponCode = null) => {
    return apiRequest('/subscription/create-subscription-session', {
      method: 'POST',
      body: JSON.stringify({
        plan_id: planId,
        payment_method: "all",
        success_url: successUrl,
        cancel_url: cancelUrl,
        first_name: customerInfo.first_name,
        last_name: customerInfo.last_name,
        phone_number: customerInfo.phone_number,
        coupon_code: couponCode
      })
    });
  },

  // Get current user's subscription details
  getCurrentSubscription: async () => {
    return apiRequest('/subscription/subscription');
  },

  // Cancel user's subscription
  cancelSubscription: async (cancelImmediately = false, cancellationReason = null) => {
    return apiRequest('/subscription/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({
        cancel_immediately: cancelImmediately,
        cancellation_reason: cancellationReason
      })
    });
  },

  // Get current billing period usage statistics
  getUsageStats: async () => {
    return apiRequest('/subscription/usage');
  },

  // Get user's payment history
  getPaymentHistory: async (limit = 10) => {
    return apiRequest(`/subscription/payment-history?limit=${limit}`);
  }
};

// Legal documents API
export const legalApi = {
  // Fetch all public legal documents
  listDocuments: async () => {
    return publicApiRequest('/legal');
  },

  // Fetch a specific legal document by slug
  getDocument: async (slug) => {
    return publicApiRequest(`/legal/${slug}`);
  }
};

// Coupons API
export const couponApi = {
  // Validate a promo code
  validateCoupon: async (couponCode) => {
    return apiRequest('/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ code: couponCode })
    });
  },

  // Redeem a promo code
  redeemCoupon: async (couponCode) => {
    return apiRequest('/coupons/redeem', {
      method: 'POST',
      body: JSON.stringify({ code: couponCode })
    });
  }
};
